import { ExternalLink, Paperclip } from "lucide-react";
import { useNotify } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/utils";

import { formatEuro } from "../gulaConstants";
import { round2, type AccIncome } from "./accounting";
import { spanishDate } from "./gestorPackage";
import type { IncomeRow } from "./summary";
import { openAccountingFile } from "./useAccountingData";

const FILTERS = [
  { value: "all", label: "Todo" },
  { value: "stripe", label: "Stripe" },
  { value: "manual", label: "A mano" },
] as const;

/** Income of the period: Stripe (automatic) and the ones added by hand. */
export const IncomesView = ({
  rows,
  onEdit,
}: {
  rows: IncomeRow[];
  onEdit: (income: AccIncome) => void;
}) => {
  const notify = useNotify();
  const [filter, setFilter] =
    useState<(typeof FILTERS)[number]["value"]>("all");
  const visible = rows.filter(
    (row) => filter === "all" || row.source === filter,
  );
  const totals = visible.reduce(
    (sum, row) => ({
      base: sum.base + row.base,
      vat: sum.vat + row.vat,
      total: sum.total + row.total,
    }),
    { base: 0, vat: 0, total: 0 },
  );

  const openFile = async (path: string) => {
    try {
      await openAccountingFile(path);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Error", {
        type: "error",
      });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1 rounded-lg bg-muted p-1 text-sm">
          {FILTERS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilter(option.value)}
              className={cn(
                "rounded-md px-3 py-1 font-medium",
                filter === option.value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Los cobros de Stripe entran solos (sin los reembolsados). Los de TPV o
          financiera, con «Añadir ingreso».
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Fecha</th>
              <th className="px-3 py-2 text-left font-medium">Cliente</th>
              <th className="px-3 py-2 text-left font-medium">Cobro</th>
              <th className="px-3 py-2 text-right font-medium">Base</th>
              <th className="px-3 py-2 text-right font-medium">IVA</th>
              <th className="px-3 py-2 text-right font-medium">Total</th>
              <th className="px-3 py-2 text-left font-medium">Recibo</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr
                key={row.key}
                onClick={() => (row.income ? onEdit(row.income) : undefined)}
                className={cn(
                  "border-t",
                  row.income && "cursor-pointer hover:bg-muted/40",
                )}
              >
                <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                  {spanishDate(row.day)}
                </td>
                <td className="px-3 py-2">
                  {row.clientId ? (
                    <Link
                      to={`/clientes?cliente=${row.clientId}`}
                      onClick={(event) => event.stopPropagation()}
                      className="font-medium hover:underline"
                    >
                      {row.customer}
                    </Link>
                  ) : (
                    <span className="font-medium">{row.customer || "—"}</span>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {row.concept}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-xs",
                      row.source === "stripe"
                        ? "bg-violet-100 text-violet-800 dark:bg-violet-400/15 dark:text-violet-200"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {row.method}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(row.base)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(row.vat)}
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">
                  {formatEuro(row.total)}
                </td>
                <td className="px-3 py-2">
                  {row.receiptUrl ? (
                    <a
                      href={row.receiptUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Stripe
                    </a>
                  ) : row.income?.file_path ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openFile(row.income?.file_path as string);
                      }}
                      className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                    >
                      <Paperclip className="h-4 w-4" />
                      Ver
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!visible.length ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-8 text-center text-muted-foreground"
                >
                  No hay ingresos en este periodo.
                </td>
              </tr>
            ) : null}
          </tbody>
          {visible.length ? (
            <tfoot className="border-t bg-muted/30 font-medium">
              <tr>
                <td className="px-3 py-2" colSpan={3}>
                  {visible.length} {visible.length === 1 ? "cobro" : "cobros"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(round2(totals.base))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(round2(totals.vat))}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatEuro(round2(totals.total))}
                </td>
                <td />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};
